import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetNotificationsQuery,
  useMarkNotificationAsSeenMutation,
} from "../services/appApi";
import { useNotifications } from "../context/NotificationContext";
import "./NotificationsPage.scss";

// Map the mobile "screenToOpen" names to web routes
const SCREEN_TO_PATH = {
  Calendar: "/calendar",
  CalendarScreen: "/calendar",
  Chat: "/chat",
  ChatScreen: "/chat",
  Profile: "/profile",
  ProfileScreen: "/profile",
};

// Emoji icon based on notification title (best-effort)
function getIcon(title = "") {
  const t = title.toLowerCase();
  if (t.includes("event") || t.includes("calendar")) return "📅";
  if (t.includes("chat") || t.includes("message")) return "💬";
  if (t.includes("welcome")) return "🎉";
  if (t.includes("remind")) return "⏰";
  if (t.includes("update")) return "🔔";
  return "🔔";
}

function NotificationsPage() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [notificationsList, setNotificationsList] = useState([]);
  const { clearUnreadCount, refetchTick } = useNotifications();

  const { data, isLoading, isFetching, refetch } = useGetNotificationsQuery(page);
  const [markAsSeen] = useMarkNotificationAsSeenMutation();

  // Intersection observer sentinel for infinite scroll
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  // ── On mount: clear badge + do a fresh page-1 fetch ──────────────────────
  useEffect(() => {
    clearUnreadCount();
    setPage(1);
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── When a live socket notification arrives, snap back to page 1 ──────────
  useEffect(() => {
    if (refetchTick > 0) {
      setPage(1);
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchTick]);

  // ── Accumulate pages as the user scrolls ──────────────────────────────────
  useEffect(() => {
    if (data?.notifications) {
      if (page === 1) {
        setNotificationsList(data.notifications);
      } else {
        setNotificationsList((prev) => {
          const existingIds = new Set(prev.map((n) => n._id));
          const fresh = data.notifications.filter((n) => !existingIds.has(n._id));
          return [...prev, ...fresh];
        });
      }
    }
  }, [data, page]);

  // ── Infinite scroll via IntersectionObserver ──────────────────────────────
  const setupObserver = useCallback(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && data && page < data.totalPages && !isFetching) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.5 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }
  }, [data, page, isFetching]);

  useEffect(() => {
    setupObserver();
    return () => observerRef.current?.disconnect();
  }, [setupObserver]);

  // ── Handle card click: mark as read + deep-link navigation ───────────────
  const handleClick = async (item) => {
    if (!item.isRead) {
      try {
        await markAsSeen(item.notificationId._id).unwrap();
        // Optimistically flip the card's unread state in local list
        setNotificationsList((prev) =>
          prev.map((n) =>
            n._id === item._id ? { ...n, isRead: true } : n
          )
        );
      } catch (err) {
        console.error("Failed to mark notification as seen:", err);
      }
    }

    // Deep-link navigation using the metadata stored in the notification
    const metadata = item.notificationId?.data;
    if (metadata?.screenToOpen) {
      const path = SCREEN_TO_PATH[metadata.screenToOpen];
      if (path) navigate(path);
    }
  };

  // ── Guard: not logged in ──────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="notifications-page">
        <div className="notifications-page__empty">
          <div className="empty-icon">🔒</div>
          <h3>Please log in</h3>
          <p>You must be logged in to view your notifications.</p>
        </div>
      </div>
    );
  }

  // ── Initial loading spinner ───────────────────────────────────────────────
  if (isLoading && page === 1) {
    return (
      <div className="notifications-page">
        <div className="notifications-page__loading">
          <div className="spinner" />
          <span>Loading notifications…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <div className="notifications-page__header">
        <h1>Notifications</h1>
        {notificationsList.length > 0 && (
          <p className="subtitle">
            {notificationsList.filter((n) => !n.isRead).length > 0
              ? `${notificationsList.filter((n) => !n.isRead).length} unread`
              : "All caught up!"}
          </p>
        )}
      </div>

      {/* Empty state */}
      {!isFetching && notificationsList.length === 0 && (
        <div className="notifications-page__empty">
          <div className="empty-icon">🔔</div>
          <h3>No notifications yet</h3>
          <p>We'll let you know when something happens.</p>
        </div>
      )}

      {/* Notification list */}
      {notificationsList.length > 0 && (
        <div className="notifications-page__list">
          {notificationsList.map((item) => {
            const content = item.notificationId;
            if (!content) return null;

            const unread = !item.isRead;

            return (
              <div
                key={item._id}
                className={`notif-card${unread ? " notif-card--unread" : ""}`}
                onClick={() => handleClick(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleClick(item)}
                aria-label={`Notification: ${content.title}`}
              >
                <div className="notif-card__icon">
                  {getIcon(content.title)}
                </div>

                <div className="notif-card__body">
                  <p className={`notif-card__title${unread ? " notif-card__title--unread" : ""}`}>
                    {content.title}
                  </p>
                  <p className="notif-card__text">{content.body}</p>
                  <p className="notif-card__time">
                    {new Date(item.createdAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>

                {unread && <div className="notif-card__dot" aria-label="Unread" />}
              </div>
            );
          })}

          {/* Infinite-scroll sentinel */}
          <div ref={sentinelRef} style={{ height: 1 }} />
        </div>
      )}

      {/* Paginating spinner */}
      {isFetching && page > 1 && (
        <div className="notif-load-more">
          <div className="notif-load-more__spinner">
            <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
            Loading more…
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationsPage;
